/* eslint-disable react-hooks/rules-of-hooks */
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "../../../components/ui/chart";

import { ThreeDWrapper } from "../core/3d-wrapper.core";
import millify from "millify";
import { useMemo } from "react";
import CustomCursor from "../../../components/charts/chart-cursor";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import { DateTime } from "luxon";

const chartConfig = {
  category: {
    label: "Category",
  },
  value: {
    label: "Value",
    color: "hsl(var(--slider-range))",
  },
} satisfies ChartConfig;

// Function to calculate the maximum value and step size
const calculateMaxValueAndStep = (
  data: Array<{ category: string; value: number; fill: string }>,
) => {
  if (data.length === 0) {
    return { maxValue: 10, step: 2 };
  }

  let maxValue = Math.max(...data.map((d) => d.value));
  let step: number;

  if (maxValue === 0) {
    maxValue = 10;
    step = 2;
  } else if (maxValue <= 5) {
    step = 1;
    maxValue = 5;
  } else if (maxValue <= 10) {
    step = 1;
    maxValue = 10;
  } else if (maxValue <= 50) {
    step = 5;
    maxValue = 50;
  } else if (maxValue <= 100) {
    step = 10;
    maxValue = 100;
  } else if (maxValue <= 500) {
    step = 50;
    maxValue = 500;
  } else if (maxValue <= 1000) {
    step = 100;
    maxValue = 1000;
  } else {
    step = Math.ceil(maxValue / 5);
    maxValue = Math.ceil(maxValue / step) * step;
  }

  const roundedMaxValue = Math.ceil(maxValue / step) * step;
  return { maxValue: roundedMaxValue, step };
};

// Function to generate ticks
const generateTicks = (min: number, max: number, step: number) => {
  const ticks = [];
  for (let i = min; i <= max; i += step) {
    ticks.push(i);
  }
  return ticks;
};

export function Barchart({
  campaignMetrics,
  chartData,
  title,
}: {
  campaignMetrics: ICampaignMetricMinimal;
  chartData: Array<{ category: string; value: number; fill: string }>;
  title: string;
}) {
  const { maxValue, step } = useMemo(
    () => calculateMaxValueAndStep(chartData),
    [chartData],
  );

  const hasData = chartData.reduce((acc, curr) => acc + curr.value, 0) > 0;

  return (
    <>
      <style>
        {`
          .recharts-cartesian-axis-tick-value {
            fill: black !important;
            font-size: 14px !important;
            font-weight: 500 !important;
          }
        `}
      </style>
      <ThreeDWrapper className="p-0 w-full overflow-hidden" hover={false}>
        <Card className="relative bg-transparent p-0 border-border rounded-2xl w-full h-full font-cabinetgrotesk text-soloprimary">
          <CardHeader className="flex sm:flex-row items-start border-black border-b">
            <div className="flex flex-col flex-1 justify-center gap-1">
              <CardTitle className="font-medium text-2xl tracking-normal">
                {title}
              </CardTitle>
              <CardDescription className="font-bold text-secondary">
                Last updated{" "}
                {campaignMetrics.lastUpdatedAt
                  ? (() => {
                      const dt = DateTime.fromISO(
                        campaignMetrics.lastUpdatedAt.toString(),
                      );
                      return `${dt.toFormat("h:mm a")} - ${dt.toLocaleString(DateTime.DATE_SHORT)}`;
                    })()
                  : "-"}
              </CardDescription>
            </div>
            <ThreeDWrapper
              hover={false}
              className="flex border border-black rounded-lg overflow-hidden"
            >
              <button
                type="button"
                className="relative flex justify-center items-center px-6 text-left"
              >
                {chartData.map((data, index) => {
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    <div className="flex items-center" key={index}>
                      <div className="flex flex-col text-center">
                        <span className="flex items-center gap-2 font-semibold text-secondary">
                          {data.category}
                        </span>
                        <span className="font-bold text-lg sm:text-3xl leading-none">
                          {millify(data.value)}
                        </span>
                      </div>
                      {index < chartData.length - 1 && (
                        <div className="mx-8">
                          <div className="border-r h-10" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </button>
            </ThreeDWrapper>
          </CardHeader>
          <CardContent className="px-2 sm:p-6">
            <ChartContainer
              config={chartConfig}
              className="w-full h-[250px] aspect-auto"
            >
              {hasData ? (
                <BarChart
                  dataKey="value"
                  data={chartData}
                  margin={{
                    left: 20,
                    right: 12,
                    top: 1,
                  }}
                >
                  <CartesianGrid
                    stroke="#2f2f2f50"
                    vertical={false}
                    strokeDasharray="4 4"
                  />
                  <XAxis
                    dataKey="category"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={12}
                    minTickGap={32}
                    className="capitalize"
                    tickFormatter={(value) => {
                      return value.toLocaleString();
                    }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={40}
                    domain={[0, maxValue]}
                    ticks={generateTicks(0, maxValue, step)}
                    tickFormatter={(value) => millify(value)}
                  />

                  <Bar
                    barSize={40}
                    radius={2}
                    dataKey="value"
                    fill={chartData[0]?.fill}
                    cursor={"pointer"}
                    strokeWidth="1"
                    stroke="black"
                  />

                  <ChartTooltip
                    cursor={
                      <CustomCursor
                        stroke="black"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                        maxValue={maxValue}
                      />
                    }
                    content={(props) => {
                      if (
                        props.active &&
                        props.payload &&
                        props.payload.length
                      ) {
                        const data = props.payload[0].payload;
                        return (
                          <div className="relative right-[70%] bg-white rounded w-12">
                            <div className="relative flex flex-col items-center px-2 py-1 border border-black rounded font-semibold">
                              <span className="flex">
                                {millify(data.value)}
                              </span>
                              <div className="-bottom-[7px] z-10 absolute bg-white border-black border-r border-b rounded-tl-full w-3 h-3 rotate-45" />
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </BarChart>
              ) : (
                <div className="flex flex-col justify-center items-center h-full font-normal">
                  <p className="text-base text-black text-center">
                    Analysing data...
                  </p>
                  <p className="text-center text-secondary text-xs">
                    Check again in some time
                  </p>
                </div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </ThreeDWrapper>
    </>
  );
}
